// Pure save-section helper functions for the place-selection Review flow.
//
// These are intentionally free of any Vue/component or Vuex dependency so that
// fast-check property tests can import and exercise them directly. They mirror
// the extraction pattern used by placeSelectionDrawHelpers.js and
// placeSelectionCsv.js in this same folder.
//
// The store mutation (`markSelectedPlaceSaved`) and the Review component
// computeds delegate to these helpers, so the property tests exercise the same
// code paths that production uses.
// Feature: place-selection-save-section (Task 1.1)

/**
 * The `_source` values whose places are considered "saveable" (i.e. not yet
 * persisted to the library) and therefore rendered in the Places to Save
 * section.
 * @type {string[]}
 */
export const SAVEABLE_SOURCES = ["draw", "upload", "csv"];

/**
 * Human-readable sub-group headers for each saveable source, shown inside the
 * Places to Save section.
 * @type {{ draw: string, upload: string, csv: string }}
 */
export const SOURCE_LABELS = {
  draw: "Drawn",
  upload: "Uploaded",
  csv: "CSV Upload",
};

/**
 * The display order for saveable sub-groups within the Places to Save section.
 * Lower numbers render first.
 * @type {{ draw: number, upload: number, csv: number }}
 */
export const SAVEABLE_ORDER = {
  draw: 0,
  upload: 1,
  csv: 2,
};

/**
 * Classify a single place as `'saveable'` or `'library'` based on its
 * `_source`. Any place whose `_source` is not in {@link SAVEABLE_SOURCES} —
 * including `library`, `null`, missing, or an unknown/junk value — classifies
 * as `'library'`.
 *
 * @param {Object|null|undefined} place - The selected place.
 * @returns {'saveable'|'library'} The classification bucket.
 */
export function classifyPlace(place) {
  const source = place && place._source;
  return SAVEABLE_SOURCES.includes(source) ? "saveable" : "library";
}

/**
 * Partition an array of selected places into the two Review sections.
 *
 * `saveable` is an array of `{ source, label, items }` sub-groups, one per
 * saveable source that has at least one place, ordered by {@link SAVEABLE_ORDER}
 * (empty sources produce no sub-group). `library` is a flat array of every
 * non-saveable place, in original order.
 *
 * The flattened `saveable` items together with `library` form a partition of
 * the input: their union equals the input multiset and they are disjoint.
 *
 * @param {Array<Object>} places - The selected places.
 * @returns {{ saveable: Array<{source: string, label: string, items: Array<Object>}>, library: Array<Object> }}
 */
export function partitionPlaces(places) {
  const source = Array.isArray(places) ? places : [];

  const buckets = {};
  const library = [];

  for (const place of source) {
    if (classifyPlace(place) === "saveable") {
      const key = place._source;
      if (!buckets[key]) {
        buckets[key] = [];
      }
      buckets[key].push(place);
    } else {
      library.push(place);
    }
  }

  const saveable = SAVEABLE_SOURCES.slice()
    .sort((a, b) => SAVEABLE_ORDER[a] - SAVEABLE_ORDER[b])
    .filter((key) => buckets[key] && buckets[key].length > 0)
    .map((key) => ({
      source: key,
      label: SOURCE_LABELS[key],
      items: buckets[key],
    }));

  return { saveable, library };
}

/**
 * Classify a library place as `'custom'` or `'azira'` by origin.
 *
 * Mirrors the backend `isCustomPlace` rule (pinnacle-api
 * lib/update_custom_place_helpers.js): a place is Custom when EITHER its
 * `custom` flag is `true` OR the second underscore-separated segment of
 * `placematrix_id`, uppercased, equals `'C'`. The explicit `custom` flag is
 * required because Custom-library POIs pulled from the Places API v3 search do
 * not always carry a `placematrix_id` whose token is `'C'`; without it, places
 * selected from the Custom library were misclassified as `'azira'`. Everything
 * else — including Azira catalog places and any place with a missing/malformed
 * `placematrix_id` — classifies as `'azira'`.
 *
 * @param {Object|null|undefined} place - A library place.
 * @returns {'custom'|'azira'} The library origin bucket.
 */
export function classifyLibraryPlace(place) {
  if (place && place.custom === true) return "custom";
  const token = (String((place && place.placematrix_id) || "").split("_")[1] || "").toUpperCase();
  return token === "C" ? "custom" : "azira";
}

/**
 * Split a flat list of library places into their two origin sub-groups,
 * preserving original order within each group.
 *
 * @param {Array<Object>} places - The library places (partitionPlaces().library).
 * @returns {{ azira: Array<Object>, custom: Array<Object> }}
 */
export function partitionLibraryPlaces(places) {
  const source = Array.isArray(places) ? places : [];
  const azira = [];
  const custom = [];

  for (const place of source) {
    if (classifyLibraryPlace(place) === "custom") {
      custom.push(place);
    } else {
      azira.push(place);
    }
  }

  return { azira, custom };
}

/**
 * Count how many of the given places are saveable.
 *
 * @param {Array<Object>} places - The selected places.
 * @returns {number} The number of saveable places.
 */
export function saveCount(places) {
  const source = Array.isArray(places) ? places : [];
  return source.filter((place) => classifyPlace(place) === "saveable").length;
}

/**
 * Build the exact Save button label for the current state.
 *
 * - `'Saving...'` when a save is in progress (`isSaving` takes precedence).
 * - `` `Retry Save (${count})` `` when at least one saveable place errored.
 * - `` `Save (${count})` `` otherwise.
 *
 * `count` is rendered as a base-10 integer with no leading zeros and no
 * thousands separators.
 *
 * @param {number} count - The saveable count.
 * @param {boolean} hasErrors - Whether at least one saveable place has a save error.
 * @param {boolean} isSaving - Whether a save is in progress.
 * @returns {string} The Save button label.
 */
export function saveButtonLabel(count, hasErrors, isSaving) {
  if (isSaving) {
    return "Saving...";
  }
  const n = Number(count) || 0;
  const label = String(Math.trunc(n));
  return hasErrors ? `Retry Save (${label})` : `Save (${label})`;
}

/**
 * Normalize a mark-saved payload into an array of string ids. Accepts a single
 * id, an array of ids, `{ ids }`, or `{ id }`. Anything unrecognized (including
 * `null`/`undefined`) normalizes to an empty array.
 *
 * @param {string|string[]|{ids?: string[]}|{id?: string}|null|undefined} ids
 * @returns {Array} The normalized id list.
 */
function normalizeIds(ids) {
  if (Array.isArray(ids)) {
    return ids;
  }
  if (typeof ids === "string") {
    return [ids];
  }
  if (ids && typeof ids === "object") {
    if (Array.isArray(ids.ids)) {
      return ids.ids;
    }
    if (ids.id != null) {
      return [ids.id];
    }
  }
  return [];
}

/**
 * The candidate response field paths that may carry the newly created place's
 * canonical POI id, in priority order. The save endpoint proxies the Places API
 * v3 `places/draw/polygon` response verbatim, so the id may surface under a few
 * shapes; we probe each so the extraction is resilient to the exact envelope.
 * @type {string[]}
 */
const SAVED_POI_ID_PATHS = [
  "near_poi_id",
  "data.near_poi_id",
  "payload.near_poi_id",
  "data.data.near_poi_id",
  "poi_id",
  "data.poi_id",
  "id",
  "data.id",
];

/**
 * Read a dot-delimited path off an object without throwing on missing
 * intermediate values.
 * @param {Object} obj
 * @param {string} path
 * @returns {*} The value at `path`, or `undefined`.
 */
function readPath(obj, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    return acc[key];
  }, obj);
}

/**
 * Extract the created place's canonical POI id (`near_poi_id`) from a
 * save-custom-place / CSV-register response. Probes {@link SAVED_POI_ID_PATHS}
 * in order and returns the first non-empty string value (numbers are coerced to
 * strings). Returns `null` when no usable id is present so callers can fall back
 * to flipping the place to the library without a `near_poi_id` (the row still
 * renders; it just won't be taggable until the library is re-fetched).
 *
 * Defensive on non-object input (returns `null`).
 *
 * @param {*} response - The response body from the save/register call.
 * @returns {string|null} The extracted `near_poi_id`, or `null`.
 */
export function extractSavedPoiId(response) {
  if (!response || typeof response !== "object") {
    return null;
  }
  for (const path of SAVED_POI_ID_PATHS) {
    const value = readPath(response, path);
    if (typeof value === "string" && value.trim() !== "") {
      return value;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
  }
  return null;
}

/**
 * Immutable mark-saved reducer.
 *
 * Returns a NEW array where every place whose `_id` is in `ids` is replaced by
 * a new object with `_source: 'library'`, `custom: true`, and
 * `saveError: null` (all other fields preserved), and every other place is
 * passed through unchanged. When `poiIdsById` maps a saved place's `_id` to a
 * non-empty `near_poi_id`, that id is set on the flipped place; this is what
 * makes a freshly saved place taggable in the Review flow, which collects
 * `near_poi_id` from the Custom library group. The `custom: true` flag is
 * required because places saved through this flow (drawn / uploaded / CSV)
 * become Custom-library places; without it, `classifyLibraryPlace` would
 * misclassify the freshly saved rows as `'azira'` (they carry no
 * `placematrix_id` whose token is `'C'`), so they would appear in the Azira
 * section instead of Custom. The operation is a no-op in effect when the id set
 * is empty or matches nothing (a new array is still returned) and is
 * idempotent.
 *
 * @param {Array<Object>} places - The selected places.
 * @param {string|string[]|{ids?: string[], poiIdsById?: Object<string,string>}|{id?: string}} ids - Ids to mark saved (optionally with a `poiIdsById` map).
 * @returns {Array<Object>} A new array with matched places flipped to library.
 */
export function applyMarkSaved(places, ids) {
  const source = Array.isArray(places) ? places : [];
  const idSet = new Set(normalizeIds(ids));
  const poiIdsById =
    ids && typeof ids === "object" && !Array.isArray(ids) && ids.poiIdsById && typeof ids.poiIdsById === "object"
      ? ids.poiIdsById
      : {};

  return source.map((place) => {
    if (place && idSet.has(place._id)) {
      const next = { ...place, _source: "library", custom: true, saveError: null };
      const poiId = poiIdsById[place._id];
      if (typeof poiId === "string" && poiId.trim() !== "") {
        next.near_poi_id = poiId;
      }
      return next;
    }
    return place;
  });
}

/**
 * Select the custom (drawn/uploaded) places that should be persisted via the
 * save-custom-place path: those with a `geometry` and `_source` in
 * `{draw, upload}`. Library-sourced places are never selected.
 *
 * @param {Array<Object>} places - The selected places.
 * @returns {Array<Object>} The places to persist.
 */
export function selectCustomToPersist(places) {
  const source = Array.isArray(places) ? places : [];
  return source.filter(
    (place) =>
      place && place.geometry && ["draw", "upload"].includes(place._source)
  );
}

/**
 * Select the CSV entries that should be registered via the library
 * registration path: those with `_source === 'csv'` and a truthy `uploadPath`.
 * Library-sourced places are never selected.
 *
 * @param {Array<Object>} places - The selected places.
 * @returns {Array<Object>} The CSV entries to register.
 */
export function selectCsvToRegister(places) {
  const source = Array.isArray(places) ? places : [];
  return source.filter(
    (place) => place && place._source === "csv" && place.uploadPath
  );
}

/**
 * Build the `merged_geojson` features: one GeoJSON `Feature` per place that has
 * a defined `geometry`, and none for places without geometry. This is
 * independent of `_source`, so geometry-bearing library places are included
 * (mirroring PlaceSelectionModal.confirmSelection's merged_geojson build).
 *
 * @param {Array<Object>} places - The selected places.
 * @returns {Array<Object>} One `Feature` per geometry-bearing place.
 */
export function buildMergedFeatures(places) {
  const source = Array.isArray(places) ? places : [];
  return source
    .filter((place) => place && place.geometry)
    .map((place) => ({
      type: "Feature",
      properties: {
        name: place.name,
        near_poi_id: place.near_poi_id,
        placematrix_id: place.placematrix_id,
        unique_name: place.unique_name,
        vp_id: place.vp_id,
        centerLngLat: place.centerLngLat || null,
      },
      geometry: place.geometry,
    }));
}
// ---------------------------------------------------------------------------
// Required_Fields validation + bulk update helpers
// Feature: review-save-field-validation-bulk-update (Task 1.1)
// ---------------------------------------------------------------------------

/**
 * The Required_Fields a Saveable_Place must have (non-empty) to be saved to the
 * library. Order here is authoritative: validation messages and field iteration
 * follow this order.
 * @type {string[]}
 */
export const REQUIRED_SAVE_FIELDS = ["address", "category", "brand"];

/**
 * Capitalized display labels for the Required_Fields, used when building the
 * human-readable Row_Error message.
 * @type {{ address: string, category: string, brand: string }}
 */
const REQUIRED_FIELD_LABELS = {
  address: "Address",
  category: "Category",
  brand: "Brand",
};

/**
 * True when a value is an Empty_Value: `null`, `undefined`, or a string that is
 * empty or whitespace-only after `trim()`. Non-string, non-null values (e.g.
 * numbers, booleans, objects) are treated as present.
 *
 * @param {*} value - The value to test.
 * @returns {boolean} `true` when the value is considered empty/missing.
 */
export function isEmptyValue(value) {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim().length === 0;
  }
  return false;
}

/**
 * Build the human-readable Row_Error message for a set of missing Required_Fields,
 * e.g. `"Missing required field(s): Address, Category"`. Field keys are mapped
 * to capitalized labels and ordered by {@link REQUIRED_SAVE_FIELDS}; unknown or
 * duplicate keys are ignored. Returns an empty string when nothing is missing.
 *
 * Defensive on non-array input (treated as no missing fields).
 *
 * @param {string[]} missing - The missing Required_Field keys.
 * @returns {string} The Row_Error message, or `''` when nothing is missing.
 */
export function missingFieldsMessage(missing) {
  const source = Array.isArray(missing) ? missing : [];
  const missingSet = new Set(source);

  const labels = REQUIRED_SAVE_FIELDS.filter((field) =>
    missingSet.has(field)
  ).map((field) => REQUIRED_FIELD_LABELS[field]);

  if (labels.length === 0) {
    return "";
  }

  return `Missing required field(s): ${labels.join(", ")}`;
}

/**
 * Compute the missing Required_Fields for a single Saveable_Place.
 *
 * The evaluation depends on the place's source:
 *
 * - CSV_Place (`_source === 'csv'`): Required_Fields are evaluated at the
 *   FILE-ROW level.
 *     - `address` is satisfied when the CSV's own rows carry addresses
 *       (`csvSummary.hasAddress === true`) OR the file-level `address` field is
 *       non-empty. When `csvSummary.hasAddress` is absent it defaults to
 *       `false`, so address falls back to the file-level `address` (fail-safe).
 *     - `category` is satisfied when the file-level `category` is non-empty.
 *     - `brand` is satisfied when the file-level `brand` is non-empty.
 * - Editable_Place (`draw` / `upload`): each Required_Field is read directly off
 *   the place and checked with {@link isEmptyValue}.
 *
 * Missing fields are returned in {@link REQUIRED_SAVE_FIELDS} order.
 *
 * @param {Object} place - A Saveable_Place.
 * @returns {string[]} The Required_Field keys that are missing (in canonical order).
 */
export function missingRequiredFields(place) {
  const isCsv = place && place._source === "csv";

  if (isCsv) {
    const summary = place && place.csvSummary;
    const hasAddress = !!(summary && summary.hasAddress === true);
    const addressSatisfied = hasAddress || !isEmptyValue(place && place.address);

    return REQUIRED_SAVE_FIELDS.filter((field) => {
      if (field === "address") {
        return !addressSatisfied;
      }
      return isEmptyValue(place && place[field]);
    });
  }

  return REQUIRED_SAVE_FIELDS.filter((field) =>
    isEmptyValue(place && place[field])
  );
}

/**
 * Validate the Saveable_Places within `places` for Required_Fields presence.
 *
 * Non-saveable places (`library` / `null` / missing / unknown `_source`) are
 * ignored entirely — they never appear in the result regardless of their field
 * values — because {@link classifyPlace} is used to decide saveable vs not, so
 * validation agrees with the Save_Section grouping already rendered.
 *
 * For a CSV_Place (`_source === 'csv'`) Required_Fields are evaluated at the
 * file-row level; for an Editable_Place (`draw` / `upload`) each Required_Field
 * is checked directly with {@link isEmptyValue}. See
 * {@link missingRequiredFields} for the exact rules.
 *
 * `messagesById` maps each failing place's `_id` to its
 * {@link missingFieldsMessage} text so the component can surface a per-row
 * `Row_Error` through the shared `setSelectedPlaceSaveErrors` channel.
 *
 * Defensive on non-array input (treated as no places → `valid: true`).
 *
 * @param {Array<Object>} places - selectedPlaces from the session store.
 * @returns {{
 *   valid: boolean,
 *   failures: Array<{ id: string, missing: string[], message: string }>,
 *   failedIds: string[],
 *   messagesById: Object<string, string>
 * }}
 */
export function validateSaveablePlaces(places) {
  const source = Array.isArray(places) ? places : [];

  const failures = [];
  const failedIds = [];
  const messagesById = {};

  for (const place of source) {
    if (classifyPlace(place) !== "saveable") {
      continue;
    }

    const missing = missingRequiredFields(place);
    if (missing.length === 0) {
      continue;
    }

    const id = place._id;
    const message = missingFieldsMessage(missing);
    failures.push({ id, missing, message });
    failedIds.push(id);
    messagesById[id] = message;
  }

  return {
    valid: failures.length === 0,
    failures,
    failedIds,
    messagesById,
  };
}

/**
 * Compute the per-place field updates a bulk apply would make, WITHOUT mutating
 * the input `places` array, its elements, or the `input` object. Returns one
 * entry per Saveable_Place that would actually change under the given input and
 * Overwrite semantics; places that would not change (and all non-saveable
 * places) are omitted.
 *
 * Semantics (Req 4.7, 5.1-5.4, 6.1, 6.2):
 *   - `category` is only considered when `!isEmptyValue(input.category)`;
 *     likewise `brand`. When a field's input is an Empty_Value, that field is
 *     never set on any place.
 *   - Values are trimmed before being applied.
 *   - `overwrite === false` (fill-empty-only): a field is set only on Saveable_Places
 *     whose current value for that field `isEmptyValue`.
 *   - `overwrite === true`: a field is set on every Saveable_Place (for each
 *     field whose input is non-empty).
 *   - Non-saveable places (library / null / unknown source) are never included.
 *   - When both inputs are empty, or there are no Saveable_Places, the result is `[]`.
 *
 * Defensive on non-array `places` and non-object `input` (both treated as
 * yielding no updates).
 *
 * @param {Array<Object>} places - selectedPlaces from the session store.
 * @param {{ category?: string, brand?: string, overwrite?: boolean }} input - Bulk_Panel input.
 * @returns {Array<{ id: string, category?: string, brand?: string }>} One entry per changing Saveable_Place.
 */
export function computeBulkFieldUpdates(places, input) {
  const source = Array.isArray(places) ? places : [];
  const safeInput = input && typeof input === "object" ? input : {};

  const overwrite = safeInput.overwrite === true;

  const categoryProvided = !isEmptyValue(safeInput.category);
  const brandProvided = !isEmptyValue(safeInput.brand);

  // Nothing to apply: both inputs empty -> no updates.
  if (!categoryProvided && !brandProvided) {
    return [];
  }

  const nextCategory = categoryProvided ? String(safeInput.category).trim() : undefined;
  const nextBrand = brandProvided ? String(safeInput.brand).trim() : undefined;

  const updates = [];

  for (const place of source) {
    if (classifyPlace(place) !== "saveable") {
      continue;
    }

    const entry = {};

    if (categoryProvided && (overwrite || isEmptyValue(place.category))) {
      entry.category = nextCategory;
    }
    if (brandProvided && (overwrite || isEmptyValue(place.brand))) {
      entry.brand = nextBrand;
    }

    // Only include places that would actually change.
    if (Object.prototype.hasOwnProperty.call(entry, "category") ||
        Object.prototype.hasOwnProperty.call(entry, "brand")) {
      entry.id = place._id;
      updates.push(entry);
    }
  }

  return updates;
}

// ---------------------------------------------------------------------------
// Address autofill helpers (Bulk edit → "Autofill missing addresses with city")
// Feature: review-bulk-autofill-address (Task 1)
// ---------------------------------------------------------------------------

/**
 * The `_source` values whose places carry a single editable `address` field and
 * are therefore eligible for the address autofill. This deliberately excludes
 * `csv` (whose addresses live at the file-row level, not a single field) and
 * `library` (already persisted).
 * @type {string[]}
 */
export const ADDRESS_AUTOFILL_SOURCES = ["draw", "upload"];

/**
 * Select the Saveable_Places that should have their address autofilled: those
 * whose `_source` is in {@link ADDRESS_AUTOFILL_SOURCES} AND whose `address`
 * is currently an Empty_Value. Places that already have an address are never
 * returned (autofill fills only the blanks), and non-eligible sources
 * (`csv` / `library` / unknown) are excluded.
 *
 * Defensive on non-array input (treated as no places -> `[]`).
 *
 * @param {Array<Object>} places - selectedPlaces from the session store.
 * @returns {Array<Object>} The places needing an address, in original order.
 */
export function selectSaveableNeedingAddress(places) {
  const source = Array.isArray(places) ? places : [];
  return source.filter(
    (place) =>
      place &&
      ADDRESS_AUTOFILL_SOURCES.includes(place._source) &&
      isEmptyValue(place.address)
  );
}

/**
 * Resolve the address value to fill for a single place from its reverse-geocode
 * result and own name, following the fallback chain city -> country -> name.
 * Each candidate is trimmed and the first non-empty one wins; when all are
 * empty the result is `''`.
 *
 * @param {{ city?: string, country?: string, name?: string }} [parts]
 * @returns {string} The resolved address, or `''` when nothing is available.
 */
export function resolveAutofillAddress(parts = {}) {
  const candidates = [parts.city, parts.country, parts.name];
  for (const candidate of candidates) {
    if (!isEmptyValue(candidate)) {
      return String(candidate).trim();
    }
  }
  return "";
}
